import { ApplicationNylasConnectedEmail, ClientApplication, ClientUser, UserNylasEmails, UserRole } from '@numeo/types-core'

// Define a type for raw user data that might come from the server with _id instead of userId
interface RawUserData {
    userId?: string
    email: string
    name?: string
    role: UserRole
    nylasEmails: UserNylasEmails[]
}

/**
 * Type definition for application sanitization rules
 */
type SanitizationRule = (app: ClientApplication | null, user: ClientUser | null) => ClientApplication | null

/**
 * Filter nylas emails for non-admin users: keep current user's emails plus every user's primary
 * Nylas email so that email-sender resolution ("Sender: Admin" / "Sender: Dispatcher")
 * works when a dispatcher is logged in (isUserEmailConnected can resolve for any user).
 * A verify that leaves `users` out (`?omit=users`) marks the members' primary mailboxes `memberPrimary` instead.
 */
const sanitizeNylasEmails: SanitizationRule = (app, user) => {
    if (!app || !user || user.role === UserRole.ADMIN || !app.integrations?.nylas?.emails) return app

    const appClone = JSON.parse(JSON.stringify(app))
    const orgEmails = appClone.integrations.nylas.emails as ApplicationNylasConnectedEmail[]

    const currentUserEmails = new Set((user.nylasEmails || []).map((e) => e.email))
    const primaryEmailsOfAllUsers = new Set<string>()
    if (Array.isArray(app.users)) {
        for (const u of app.users) {
            const primary = u.nylasEmails?.find((e) => e.isPrimary)?.email
            if (primary) primaryEmailsOfAllUsers.add(primary)
        }
    }

    appClone.integrations.nylas.emails = orgEmails.filter(
        (email: ApplicationNylasConnectedEmail) =>
            currentUserEmails.has(email.email) || primaryEmailsOfAllUsers.has(email.email) || email.memberPrimary === true
    )

    return appClone
}

/**
 * Add userId field to all users in the application
 * This rule applies to all users regardless of role
 */
const addUserIds: SanitizationRule = (app) => {
    if (!app || !app.users || app.users.length === 0) return app

    // Create a deep clone of the app object to avoid modifying read-only properties
    const appClone = JSON.parse(JSON.stringify(app))

    // eslint-disable-next-line
    appClone.users = appClone.users.map((u: any) => ({
        ...u,
        userId: u._id?.toString() || u.id,
    }))

    return appClone
}

/**
 * Filter out users whose deletedAt is not null
 */
const filterDeletedUsers: SanitizationRule = (app) => {
    if (!app || !app.users || app.users.length === 0) return app

    // Create a deep clone of the app object to avoid modifying read-only properties
    const appClone = JSON.parse(JSON.stringify(app))

    // Use type assertion to access deletedAt property that may exist in the raw data
    appClone.users = appClone.users.filter((user: ClientUser) => !user.deletedAt)

    return appClone
}

/**
 * Keep the members already in the store when a response carries no `users` at all.
 *
 * Several endpoints answer with the application read under `{ projection: { users: 0 } }` — creating a truck
 * is one — because they have no reason to ship every member back. Storing that verbatim EMPTIES the member
 * list for as long as it takes the next `/auth/verify` to land, and everything read from it goes with it:
 * the driver and dispatcher pickers, and `boxTruckAccess`, which decides whether Profiles is in Box Truck
 * mode. An open Box Truck form would repaint itself as the TMS one mid-save.
 *
 * An ABSENT array is the signal — a present empty one is a real answer and is stored as given.
 */
export const preserveMembersWhenOmitted = (next: ClientApplication | null, previous: ClientApplication | null): ClientApplication | null => {
    if (!next || next.users || !previous?.users) return next
    // Said out loud, because this trades a LOUD failure (every picker goes blank) for a quiet one (they show
    // the roster as of the last `/auth/verify`). The endpoint that did this should be saying so itself; until
    // one of them does, this is the only trace that a member list on screen is not freshly loaded.
    console.warn('[auth] application response carried no `users` — keeping the ones already loaded until the next verify')
    return { ...next, users: previous.users }
}

/**
 * Sanitizes the application data based on user role and permissions
 * Applies all sanitization rules in sequence
 *
 * @param app Application object to sanitize
 * @param user Current user
 * @returns Sanitized application object
 */
export const sanitizeApplication = (app: ClientApplication | null, user: ClientUser | null): ClientApplication | null => {
    if (!app) return app

    // Always apply these rules regardless of user role
    let sanitizedApp = addUserIds(app, user)
    sanitizedApp = filterDeletedUsers(sanitizedApp, user)

    // If no user, return the basic sanitized app
    if (!user) return sanitizedApp

    // For non-admin users, apply all role-based sanitization rules in sequence
    const roleBasedRules = [sanitizeNylasEmails]
    return roleBasedRules.reduce((appState, rule) => rule(appState, user), sanitizedApp)
}

/**
 * Sanitizes user data to ensure it has the correct format
 * Specifically ensures userId is set properly
 *
 * @param userData Raw user data that might come from the server
 * @returns Properly formatted User object
 */
export const sanitizeUser = (userData: RawUserData | null): ClientUser | null => {
    if (!userData) return null

    // Create a deep clone to avoid modifying the original
    const userClone = JSON.parse(JSON.stringify(userData))

    // Ensure userId is set (use _id if userId is not available)
    if (!userClone.id && userClone._id) {
        userClone.id = userClone._id.toString()
    }

    // Return the sanitized user object
    return userClone as ClientUser
}
